/**
 * Correção de um item de fatura aberta (#341): editar descrição, quantidade e preço, ou remover
 * o item. Nos dois casos o motivo é obrigatório, porque é ele que explica a linha do histórico.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import { invoiceService, type InvoiceItem } from '../../services/invoiceService';
import { formatCurrency } from '../../utils/financialHelpers';

export type ModoDaCorrecao = 'editar' | 'remover';

interface InvoiceItemDialogProps {
  invoiceId: number;
  item: InvoiceItem;
  modo: ModoDaCorrecao;
  onClose: () => void;
  onSaved: (modo: ModoDaCorrecao) => void;
}

const MOTIVO_MINIMO = 5;
const DESCRICAO_MAXIMA = 500;

/** Aceita "80,50" e "80.50": quem digita preço no Brasil usa vírgula. */
function numeroDigitado(valor: string): number {
  return Number(valor.trim().replace(',', '.'));
}

const centavos = (valor: number): number => Math.round(valor * 100) / 100;

export const InvoiceItemDialog: React.FC<InvoiceItemDialogProps> = ({
  invoiceId,
  item,
  modo,
  onClose,
  onSaved,
}) => {
  const [description, setDescription] = useState(item.description);
  const [quantity, setQuantity] = useState(String(item.quantity));
  const [unitPrice, setUnitPrice] = useState(String(Number(item.unitPrice)));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setDescription(item.description);
    setQuantity(String(item.quantity));
    setUnitPrice(String(Number(item.unitPrice)));
    setReason('');
    setErro(null);
  }, [item, modo]);

  const quantidade = numeroDigitado(quantity);
  const preco = centavos(numeroDigitado(unitPrice));
  const quantidadeValida = Number.isInteger(quantidade) && quantidade >= 1;
  const precoValido = Number.isFinite(preco) && preco > 0;
  const descricaoValida =
    description.trim().length > 0 && description.trim().length <= DESCRICAO_MAXIMA;
  const motivoValido = reason.trim().length >= MOTIVO_MINIMO;

  const mudou =
    description.trim() !== item.description ||
    quantidade !== Number(item.quantity) ||
    preco !== Number(item.unitPrice);

  const podeSalvar =
    !saving &&
    motivoValido &&
    (modo === 'remover' || (quantidadeValida && precoValido && descricaoValida && mudou));

  const subtotalResultante = quantidadeValida && precoValido ? centavos(quantidade * preco) : null;

  const salvar = async () => {
    setSaving(true);
    setErro(null);
    try {
      if (modo === 'remover') {
        await invoiceService.removeItem(invoiceId, item.id, reason.trim());
      } else {
        await invoiceService.updateItem(invoiceId, item.id, {
          description: description.trim(),
          quantity: quantidade,
          unitPrice: preco,
          reason: reason.trim(),
        });
      }
      onSaved(modo);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível salvar a correção do item.');
    } finally {
      setSaving(false);
    }
  };

  const motivoCurto = reason.length > 0 && !motivoValido;

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{modo === 'remover' ? 'Remover item da fatura' : 'Editar item da fatura'}</DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {modo === 'remover' ? (
            <Box>
              <Typography variant='body1' sx={{ fontWeight: 600 }}>
                {item.description}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {`${item.quantity} × ${formatCurrency(Number(item.unitPrice))} = ${formatCurrency(Number(item.subtotal))} deixam de ser cobrados. O atendimento não volta sozinho para a fatura.`}
              </Typography>
            </Box>
          ) : (
            <>
              <TextField
                label='Descrição'
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                error={!descricaoValida}
                helperText={descricaoValida ? undefined : `Informe a descrição, com até ${DESCRICAO_MAXIMA} caracteres.`}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label='Quantidade'
                  value={quantity}
                  onChange={(event) => setQuantity(event.target.value)}
                  error={!quantidadeValida}
                  helperText={quantidadeValida ? undefined : 'Número inteiro, a partir de 1.'}
                  inputProps={{ inputMode: 'numeric' }}
                  sx={{ width: 140 }}
                />
                <TextField
                  label='Preço unitário (R$)'
                  value={unitPrice}
                  onChange={(event) => setUnitPrice(event.target.value)}
                  error={!precoValido}
                  helperText={precoValido ? undefined : 'Valor maior que zero.'}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{ width: 200 }}
                />
              </Box>
              <Box>
                <Typography variant='body2' color='text.secondary'>
                  Subtotal resultante
                </Typography>
                <Typography variant='h6' data-testid='subtotal-resultante'>
                  {subtotalResultante === null ? '—' : formatCurrency(subtotalResultante)}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {`Antes: ${formatCurrency(Number(item.subtotal))}`}
                </Typography>
              </Box>
            </>
          )}

          <TextField
            label='Motivo'
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={motivoCurto}
            helperText={
              motivoCurto
                ? `Conte o motivo com pelo menos ${MOTIVO_MINIMO} caracteres.`
                : 'Fica registrado no histórico de alterações da fatura.'
            }
            multiline
            minRows={2}
            fullWidth
          />

          {erro && <Alert severity='error'>{erro}</Alert>}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant='contained'
          color={modo === 'remover' ? 'error' : 'primary'}
          onClick={salvar}
          disabled={!podeSalvar}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {modo === 'remover' ? 'Remover da fatura' : 'Salvar alteração'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceItemDialog;
